import {
  Box,
  Button,
  Flex,
  JaneGoldIcon,
  Typography,
} from '@iheartjane/reefer';
import type {
  BoxProps,
  ButtonVariant,
  IconSize,
  MarginProperties,
  PaddingProperties,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';

import { JaneGoldLearnMoreLink } from '../janeGoldLearnMoreLink/janeGoldLearnMoreLink';
import { OhioGoldEducation } from '../ohioGoldEducation/ohioGoldEducation';

import styles from './janeGoldUserCallout.module.css';

export interface JaneGoldUserCalloutProps
  extends MarginProperties, PaddingProperties {
  borderRadius?: BoxProps['borderRadius'];
  boxShadow?: boolean;
  branded?: boolean;
  buttonVariant?: ButtonVariant;
  disableMobileStyling?: boolean;
  iconSize?: IconSize;
  isSegmentedDeal?: boolean;
  learnMoreLink?: React.ReactNode;
  onLinkAccountClick?: () => void;
  onLinkAccountTo?: string;
  showDefaultLearnMoreLink?: boolean;
  storeState?: string | null;
  userHasLinkedBank: boolean;
  userSignedIn: boolean;
}

export const JaneGoldUserCallout = ({
  branded,
  borderRadius = 'lg',
  boxShadow,
  buttonVariant = 'secondary',
  disableMobileStyling = false,
  iconSize = 'md',
  isSegmentedDeal,
  learnMoreLink,
  onLinkAccountClick,
  onLinkAccountTo,
  showDefaultLearnMoreLink = true,
  storeState,
  userHasLinkedBank,
  userSignedIn,
  ...spacingProps
}: JaneGoldUserCalloutProps) => {
  const isGoldUser = userSignedIn && userHasLinkedBank;
  const isOhioStore = storeState === 'Ohio';

  if (isGoldUser && !isOhioStore) {
    return null;
  }

  return (
    <Flex
      className={clsx(styles['calloutContainer'], {
        [styles['calloutContainer__mobile']]: !disableMobileStyling,
        [styles['boxShadow']]: boxShadow,
      })}
      borderRadius={borderRadius}
      border={!boxShadow ? 'transparent-black-ten' : undefined}
      p={24}
      {...spacingProps}
    >
      <Flex>
        <Box>
          <JaneGoldIcon size={iconSize} mr={16} />
        </Box>
        <Flex flexDirection="column">
          <Typography variant="body-bold" branded={branded}>
            Earn cash back from your favorite cannabis brands with Jane Gold.
          </Typography>
          {isOhioStore && (
            <OhioGoldEducation
              learnMoreLink={learnMoreLink}
              userHasLinkedBank={isGoldUser}
            />
          )}
        </Flex>
      </Flex>
      {showDefaultLearnMoreLink &&
        !isOhioStore &&
        (learnMoreLink || (
          <JaneGoldLearnMoreLink userHasLinkedBank={isGoldUser} />
        ))}
      {!isGoldUser && (
        <Button
          className={styles['calloutButton']}
          variant={buttonVariant}
          branded={branded}
          label={
            !userSignedIn
              ? isSegmentedDeal
                ? 'Sign in for eligibility'
                : 'Sign in'
              : 'Link bank account'
          }
          onClick={onLinkAccountClick}
          to={onLinkAccountTo}
        />
      )}
    </Flex>
  );
};
